import {css} from '@mui/material';
import {ConfigObjectType, LoadingSpinner} from '@wallaby-ui/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {ALERT_EPISODE_GROUPS, AlertEpisodeGroupDto, AlertEpisodePanelDto, AlertEpisodeRowDto} from './alert_types';
import {AlertCriteriaRenderer} from './AlertCriteriaChips';
import {AlertEpisodeGroup} from './AlertEpisodeGroup';
import {useAlertEpisodeDisposition, useAlertSnoozeOptions} from './useAlertEpisodePanel';
import {PANEL_GROUP_COLLAPSE_TIMEOUT} from '../LightPanelGroup';

type Props = {
  config: ConfigObjectType;
  /** Key prefix of the persisted group open/closed state (per page). */
  storageIdPrefix: string;
  panel: AlertEpisodePanelDto | undefined;
  isLoading: boolean;
  isError: boolean;
  /** Re-reads the panel after an action (the row may have moved group) or a 404. */
  refetch: () => void;
  /** Rendered as the actor of a system change in the episode tooltip. */
  systemActorName?: string;
  /** Product renderer of the criteria chips in the episode tooltip; the generic chips otherwise. */
  renderCriteria?: AlertCriteriaRenderer;
  /** An episode to bring into view once the panel has it (a link from the alerts list, PDW-405): its group opens and the
   * panel scrolls to it when it is out of sight. */
  focusedEpisodeId?: number;
  /** Called once the focused episode is in view, or when the loaded panel does not have it. */
  onFocusedEpisodeShown?: () => void;
  /** Where an episode's name leads (the product's route of its anchor), null for no link. Only the Elsewhere group links
   * its rows (PDW-436): the other two are about the hospitalization already open. */
  getEpisodeHref?: (row: AlertEpisodeRowDto) => string | null;
};

const panelCss = css`
  position: relative;
  padding: 0 0 8px 0;
`;
const stateCss = css`
  padding: 12px 4px;
  font-size: 13px;
  color: var(--dlThemeGray-500);
`;
const overlayCss = css`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.5);
  z-index: 2;
`;

/** Every group in the fixed order, an empty one included, so the panel always shows all three. */
function groupsInOrder(panel: AlertEpisodePanelDto): AlertEpisodeGroupDto[] {
  return ALERT_EPISODE_GROUPS.map((id) => panel.groups.find((g) => g.group === id) ?? {group: id, count: 0, rows: []});
}

/**
 * The Alerts tab of the right panel (PDW-182 / PDW-245): the three collapsible groups of the patient's episodes with
 * their actions, right below the tabs as in the design (the tab carries the active count, so no panel title repeats it). The data is the BE's, grouped and sorted
 * there; the panel only renders it and posts actions. Loading and error states are the app's usual ones.
 */
export const AlertEpisodePanel: React.FC<Props> = (props) => {
  const {
    config,
    storageIdPrefix,
    panel,
    isLoading,
    isError,
    refetch,
    systemActorName = 'MERIE',
    renderCriteria,
    focusedEpisodeId,
    onFocusedEpisodeShown,
    getEpisodeHref,
  } = props;
  const intl = useIntl();
  const panelRef = useRef<HTMLDivElement>(null);
  const focusedGroup =
    focusedEpisodeId === undefined ? undefined : panel?.groups.find((g) => g.rows.some((r) => r.episodeId === focusedEpisodeId))?.group;
  useEffect(() => {
    if (focusedEpisodeId === undefined || !panel) return undefined;
    if (!focusedGroup) {
      onFocusedEpisodeShown?.();
      return undefined;
    }
    // once the group has opened (it may have been collapsed); `nearest` leaves a row already in sight where it is
    const timer = window.setTimeout(() => {
      panelRef.current?.querySelector(`[data-episode-id="${focusedEpisodeId}"]`)?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
      onFocusedEpisodeShown?.();
    }, PANEL_GROUP_COLLAPSE_TIMEOUT + 50);
    return () => window.clearTimeout(timer);
  }, [focusedEpisodeId, focusedGroup, panel, onFocusedEpisodeShown]);
  const [snoozeNeeded, setSnoozeNeeded] = useState(false);
  const fetchedSnoozeOptions = useAlertSnoozeOptions(config, snoozeNeeded && !panel?.snoozeOptions?.length);
  const snoozeOptions = panel?.snoozeOptions?.length ? panel.snoozeOptions : fetchedSnoozeOptions;
  const onSnoozeOptionsNeeded = useCallback(() => setSnoozeNeeded(true), []);
  const {act, isPending} = useAlertEpisodeDisposition(config, refetch);
  return (
    <div css={panelCss} data-name={'dl-alert-episode-panel'} ref={panelRef}>
      {isLoading && !panel && <LoadingSpinner isVisible marginTop={16} />}
      {isError && <div css={stateCss}>{intl.formatMessage({id: 'alert.panel.loadError'})}</div>}
      {panel &&
        groupsInOrder(panel).map((group, index) => (
          <AlertEpisodeGroup
            key={group.group}
            group={group}
            first={index === 0}
            forceOpen={group.group === focusedGroup}
            storageIdPrefix={storageIdPrefix}
            snoozeOptions={snoozeOptions}
            onSnoozeOptionsNeeded={onSnoozeOptionsNeeded}
            onAction={act}
            actionsDisabled={isPending}
            systemActorName={systemActorName}
            renderCriteria={renderCriteria}
            getEpisodeHref={group.group === 'ELSEWHERE' ? getEpisodeHref : undefined}
          />
        ))}
      {isPending && <div css={overlayCss} />}
    </div>
  );
};

AlertEpisodePanel.displayName = 'AlertEpisodePanel';
